import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import Moveable from 'react-moveable';
import {
	OnDrag,
	OnDragEnd,
	OnRotate,
	OnRotateEnd,
	OnResize,
	OnResizeEnd,
} from 'react-moveable/declaration/types';
import { useDispatch, useSelector } from 'react-redux';
import Socket from '../../connection/socket';
import useShots from '../../contexts/shots/provider';
import {
	editGraphicsAction,
	clearHiddenGraphicsForShowBuilder,
	removeShotModalGraphic,
	setCurrentGraphicsId,
} from '../../stores/actions/graphics';
import { editShot } from '../../stores/actions/shots';
import { setOpenContextMenuId } from '../../stores/actions/user-interface';
import { GraphicInterface } from '../../stores/reducers/graphics';
import { Shot } from '../../stores/reducers/shots';
import { GreenroomState as GS } from '../../stores/types/types';
import { ContentTemplate } from '../types/stylepacks';
import { openStrictStylePackMenu } from '../../utils/content-templates';
import { compareGraphic } from '../../utils/custom-graphics';
import usePrevious from '../../utils/use-previous';
import {
	getVideoPoster,
	isVideo,
} from '../../utils/utils';
import CustomGraphic from './custom-graphic';
import GraphicsContextMenu from './graphics-context-menu';
import GreenBox from './green-box';
import { ROOM_STATES } from '../../stores/reducers/settings';
import {
	updateOverlayInAllAnimatedOverlays,
	updateOverlayInAllShotOverlays,
} from '../../connection/graphics';
import { setActiveTab, setRightDrawerOpen } from '../../stores/actions/tabs';
import {
	META_MENU_ROUTES,
	META_MENU_STATES,
	TABS
} from '../../stores/types/tabs';
import { setSelectedMediaTemplate, upsertAssociatedContent } from '../../stores/actions/generate-content';
import useCanvasConfig from '../../hooks/use-canvas-config';
import { ENABLER_THUMBNAIL_STATES, EnablerStates } from '../../stores/types/session';

// percentage is 50 not 0.5
export const percentToPx = (
	elPerc: number,
	containerPx: number,
) => (elPerc / 100) * containerPx;
export const pxToPercent = (
	elPx: number,
	containerPx: number,
) => (elPx / containerPx) * 100;

export enum CanvasLocation {
	ShotTab = 'shot-tab',
	ShotEditor = 'shot-editor',
}

interface AnimationTimeout {
	[key: number]: NodeJS.Timeout;
}

interface Props {
	renderedGraphics: Array<number> | undefined
	canEdit: boolean // if in live canvas, disable editing. If showbuilder, enable
	width: number;
	height: number;
	shot?: Shot
	isForShotEditorModal?: boolean
	socket?: Socket | null
	location?: CanvasLocation | string;
	isThumbnail?: boolean;
}

export default function CustomGraphicsCanvas(props: Props) {
	const {
		renderedGraphics = [],
		canEdit,
		width,
		height,
		shot = null,
		isForShotEditorModal = false,
		socket = null,
		location = '',
		isThumbnail = false,
	} = props;

	const graphics = useSelector((s: GS) => s.GraphicsReducer.graphics);
	const host = useSelector((s: GS) => s.SessionReducer.host);
	const shotModalGraphics = useSelector((s: GS) => s.GraphicsReducer.shotModalGraphics);
	const currentGraphicId = useSelector((s: GS) => s.GraphicsReducer.currentGraphicId);
	const enabledOverlays = useSelector((s: GS) => s.GraphicsReducer.enabledOverlays);
	const animatedOverlays = useSelector((s: GS) => s.GraphicsReducer.animatedOverlays);
	const contentTemplates = useSelector((s: GS) => s.GeneratedContentReducer.contentTemplates);
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const userId = useSelector((s: GS) => s.SessionReducer.user?.gr_user);
	const shotModalShot = useSelector((s: GS) => s.ShotsReducer.shotModalShot);
	const shotStartTimestampEntity = useSelector(
		(s: GS) => s.ShotsReducer.shotStartTimestampEntity?.entityValue?.start,
	);
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const isPreviewAnimationPlaying = useSelector(
		(s: GS) => s.GraphicsReducer.isPreviewAnimationPlaying,
	);
	const disabledShowBuilderOverlays = useSelector(
		(s: GS) => s.GraphicsReducer.disabledShowBuilderOverlays,
	);
	const currentShotId = useSelector((s: GS) => s.ShotsReducer.currentShotId);
	const { metaMenuState, metaMenuActiveRoute } = useSelector((s: GS) => s.TabsReducer);
	const displayedStylePackInfo = useSelector((s: GS) => s.UserInterfaceReducer.displayedStylePackInfo);
	const globalIsDragging = useSelector((s: GS) => s.UserInterfaceReducer.globalIsDragging);
	const enablerState = useSelector((s: GS) => s.MeetingReducer.enablerState);
	const useNewShotThumbs = useSelector((s: GS) => s.SettingsReducer.featureFlags.use_new_shot_thumbs);

	const [canvasWidth, setCanvasWidth] = useState(width);
	const [canvasHeight, setCanvasHeight] = useState(height);
	const [rightClickCoords, setRightClickCoords] = useState<[number, number]>();
	const [currentGraphicEl, setCurrentGraphicEl] = useState<HTMLDivElement | null>(null);

	const prevWidth = usePrevious(canvasWidth);

	const moveableRef = useRef<Moveable | null>(null);
	const lastEnabledOverlays = useRef<number[] | null>(enabledOverlays);
	const animateInTimeouts = useRef<AnimationTimeout>({});
	const animateOutTimeouts = useRef<AnimationTimeout>({});
	const dragAreaRef = useRef<HTMLDivElement | null>(null);
	const canvasRef = useRef<HTMLDivElement>(null);

	const dispatch = useDispatch();

	const {
		editShotModalShot,
		isShotBuilderOpen,
		currentShot,
	} = useShots();

	const { showingHoldSlide } = useCanvasConfig(currentShot);

	useEffect(() => {
		if (
			currentShotId
			|| roomState !== ROOM_STATES.EDITING
		) {
			dispatch(clearHiddenGraphicsForShowBuilder());
		}
	}, [currentShotId, roomState]);

	useEffect(
		// Clean up any existing timeouts for unmount
		() => {
			const clearAllTimeouts = (timeoutsObject: AnimationTimeout): void => {
				Object.keys(timeoutsObject).forEach((key) => {
					const timeoutId = parseInt(key, 10);

					clearTimeout(timeoutsObject[timeoutId]);
				});
			};

			clearAllTimeouts(animateInTimeouts.current);
			clearAllTimeouts(animateOutTimeouts.current);
		},
		[],
	);

	const globalOverlays = useMemo(() => graphics.reduce(
		(idArray: number[], { id, is_global: isGlobal }: GraphicInterface) => {
			if (id && !!isGlobal) {
				return [...idArray, id];
			}

			return idArray;
		},
		[],
	), [graphics]);

	const allGraphics = useMemo((): GraphicInterface[] => (
		isForShotEditorModal
			? shotModalGraphics
			: graphics
	), [isForShotEditorModal, graphics, shotModalGraphics, currentGraphicId]);

	const shotGraphics = useMemo(
		() => allGraphics
			.filter((graphic: GraphicInterface) => renderedGraphics
				.includes(graphic?.id || 0) || graphic.is_global)
			.sort((a, b) => renderedGraphics
				.indexOf(a.id ? a.id : 0) - renderedGraphics.indexOf(b.id ? b.id : 0)),

		[allGraphics, renderedGraphics, currentGraphicId],
	);

	const selectedGraphic = useMemo((): GraphicInterface | null => {
		if (isThumbnail || enablerState !== EnablerStates.None) {
			return null;
		}
		return shotGraphics.find((g) => g.id === currentGraphicId) ?? null;
	}, [currentGraphicId, shotGraphics, isForShotEditorModal, isThumbnail, enablerState]);

	// The right click context menu requires a unique string id
	const contextMenuId = useMemo(() => `overlays-${selectedGraphic?.id}`, [selectedGraphic]);

	function updateSelectedGraphic(graphic: GraphicInterface | null) {
		if (isPreviewAnimationPlaying) return;

		if (graphic?.id) {
			let contentTemplate:ContentTemplate | undefined;

			if (graphic?.template_type) {
				contentTemplate = contentTemplates.find(x => x.type == graphic?.template_type);
			}

			dispatch(setCurrentGraphicsId(graphic.id));
			dispatch(setSelectedMediaTemplate(null, ''));

			if (contentTemplate?.data?.strict) {
				const isOpenToStylePack = metaMenuState === META_MENU_STATES.OPEN &&
					metaMenuActiveRoute === META_MENU_ROUTES.STYLE_PACK_INFO &&
					displayedStylePackInfo?.id === contentTemplate.stylePack;

				if (!isOpenToStylePack) {
					if (!isForShotEditorModal) {
						openStrictStylePackMenu(dispatch, contentTemplates, contentTemplate);
					}
				}
			} else {
				if (!isForShotEditorModal) {
				// edit modal has a useEffect in shot-edit-ui.tsx instead
				dispatch(setActiveTab(TABS.OVERLAYS));
				dispatch(setRightDrawerOpen(true));
				}
			}
		} else {
			dispatch(setOpenContextMenuId(null));
		}
	}

	const handleRemoveGraphic = () => {
		if (!selectedGraphic?.id || !token) {
			return;
		}

		const editedShot: Shot = JSON.parse(JSON.stringify(
			isShotBuilderOpen
				? shotModalShot
				: shot,
		));
		editedShot.shot_data.shotGraphics = editedShot.shot_data.shotGraphics
			?.filter((graphicId: number) => graphicId !== selectedGraphic.id);

		if (isShotBuilderOpen) {
			editShotModalShot(editedShot, socket);
			dispatch(removeShotModalGraphic(selectedGraphic.id));
		} else {
			dispatch(editShot(token, editedShot, meeting?.meeting_id, meeting?.channel, useNewShotThumbs));
		}

		updateSelectedGraphic(null);
	};

	const handleRemoveGraphicKey = ({ target, key }: KeyboardEvent) => {
		if (
			target instanceof HTMLInputElement
			|| target instanceof HTMLTextAreaElement
			|| (target instanceof HTMLElement && target.contentEditable === 'true')
		) return;

		if (key === 'Backspace' || key === 'Delete') {
			handleRemoveGraphic();
		}
	};

	useEffect(() => {
		setCanvasWidth(width);
	}, [width]);
	useEffect(() => {
		setCanvasHeight(height);
	}, [height]);

	useEffect(() => {
		// green selection box gets off if canvas resizes
		if (prevWidth !== canvasWidth && selectedGraphic && moveableRef.current) {
			moveableRef.current.updateTarget();
			dispatch(setOpenContextMenuId(null));
		}
	}, [prevWidth, canvasWidth, selectedGraphic]);

	useEffect(() => {
		if (
			selectedGraphic
			&& !allGraphics.some((graphic: GraphicInterface) => graphic.id === selectedGraphic?.id)) {
			// if the selected graphic is no longer in the allGraphics array, deselect it
			updateSelectedGraphic(null);
		}
	}, [allGraphics, selectedGraphic]);

	const onDrag = (e: OnDrag) => {
		if (isPreviewAnimationPlaying) return;
		const percentageFromLeft = pxToPercent(e.left, canvasWidth);
		const percentageFromTop = pxToPercent(e.top, canvasHeight);
		e.target.style.left = `${percentageFromLeft}%`;
		e.target.style.top = `${percentageFromTop}%`;
		if (dragAreaRef.current) {
			dragAreaRef.current.style.left = `${percentageFromLeft}%`;
			dragAreaRef.current.style.top = `${percentageFromTop}%`;
		}
	};

	const onDragEnd = (e: OnDragEnd) => {
		if (isPreviewAnimationPlaying) return;
		if (!token || !meeting?.meeting_id || !userId || !selectedGraphic
			|| !selectedGraphic || !e.lastEvent) return;
		const newGraphic = {
			...selectedGraphic,
			pos_x: pxToPercent(e.lastEvent.left, canvasWidth),
			pos_y: pxToPercent(e.lastEvent.top, canvasHeight),
		};
		updateSelectedGraphic(newGraphic);
		dispatch(upsertAssociatedContent(token, meeting?.meeting, userId, newGraphic, currentShotId));
	};

	const onRotate = (e: OnRotate) => {
		if (isPreviewAnimationPlaying) return;
		e.target.style.transform = e.transform;
		if (dragAreaRef.current) {
			dragAreaRef.current.style.transform = e.transform;
		}
	};

	const onRotateEnd = (e: OnRotateEnd) => {
		if (isPreviewAnimationPlaying) return;
		if (!token || !meeting?.meeting_id || !userId || !selectedGraphic || !e.lastEvent) return;
		const newGraphic = { ...selectedGraphic, rotate: e.lastEvent.rotate };
		updateSelectedGraphic(newGraphic);
		dispatch(upsertAssociatedContent(token, meeting?.meeting, userId, newGraphic, currentShotId));
	};

	const onResize = (e: OnResize) => {
		if (isPreviewAnimationPlaying) return;
		e.target.style.width = `${(e.width / canvasWidth) * 100}%`;
		e.target.style.height = `${(e.height / canvasHeight) * 100}%`;
		if (dragAreaRef.current) {
			dragAreaRef.current.style.width = `${(e.width / canvasWidth) * 100}%`;
			dragAreaRef.current.style.height = `${(e.height / canvasHeight) * 100}%`;
		}
	};

	const onResizeEnd = (e: OnResizeEnd) => {
		if (isPreviewAnimationPlaying) return;
		if (!token || !meeting?.meeting_id || !userId || !selectedGraphic) return;
		const newGraphic = {
			...selectedGraphic,
			scale_x: parseFloat(e.target.style.width.replace('%', '')),
			scale_y: parseFloat(e.target.style.height.replace('%', '')),
		};
		updateSelectedGraphic(newGraphic);
		dispatch(upsertAssociatedContent(token, meeting?.meeting, userId, newGraphic, currentShotId));
	};

	useEffect(() => {
		if (selectedGraphic) {
			moveableRef?.current?.updateTarget();
		}
	}, [selectedGraphic]);

	const handleRightClick = (e: React.MouseEvent) => {
		if (e.target instanceof HTMLElement && canEdit) {
			const elementId = e.target.id;
			const inDragArea = e.target.className === 'graphic-drag-area';
			const graphic = inDragArea
				? selectedGraphic
				: shotGraphics.find(
					(foundGraphic: GraphicInterface) => elementId === `custom-graphic-${foundGraphic?.id}${
						isShotBuilderOpen
							? '-shot-editor'
							: ''
					}`,
				);

			if (graphic) {
				e.preventDefault();
				const { pageX, pageY } = e;
				setRightClickCoords([pageX, pageY]);
				dispatch(setOpenContextMenuId(contextMenuId));
				if (graphic && graphic.id !== selectedGraphic?.id) {
					updateSelectedGraphic(graphic);
				}
			}
		}
	};

	const canvasBounds = useMemo(() => {
		if (height && width) {
			let returnedBounds = {
				left: -100,
				top: -100,
				bottom: height + 100,
				right: width + 100,
			};

			if (selectedGraphic) {
				const selectedGraphicEl = document.getElementById(`graphic-wrapper-${selectedGraphic.id}${isShotBuilderOpen ? '-shot-editor' : ''}`);
				if (selectedGraphicEl) {
					// these are how many px outside of the canvas we're allowing
					// the graphic to go (75% of it's size)
					const widthAllowedOutside = selectedGraphicEl.clientWidth * 0.75;
					const heightAllowedOutside = selectedGraphicEl.clientHeight * 0.75;

					returnedBounds = {
						left: -widthAllowedOutside,
						top: -heightAllowedOutside,
						bottom: height + heightAllowedOutside,
						right: width + widthAllowedOutside,
					};
				}
			}

			return returnedBounds;
		}
		// graphic doesn't get rendered until shotModalShot updated
		// meaning selectedGraphicEl is dependent on shotModalShot,
		// making shotModalShot a dependency of this memo
		return undefined;
	}, [shotModalShot, width, height, selectedGraphic]);

	useEffect(() => {
		if (canEdit) {
			window.addEventListener('keydown', handleRemoveGraphicKey);
		}

		return () => {
			if (canEdit) {
				window.removeEventListener('keydown', handleRemoveGraphicKey);
			}
		};
	}, [
		rightClickCoords,
		isShotBuilderOpen,
		selectedGraphic,
		shot,
		shotModalShot,
		shotGraphics,
	]);

	useEffect(() => {
		lastEnabledOverlays.current = enabledOverlays;
	}, [enabledOverlays]);

	useEffect(() => {
		// For any graphic that is built into the beginning of a shot we pre-cache
		// it here (prior to the shot being loaded) so there is no delay in seeing the
		// graphic when the shot loads. Without pre-caching there can be a moment/blip where you can
		// see what's behind the graphic when maybe the graphic was put there as a transition into
		// the content of the shot (ex: video curtains opening up). For graphic videos we load and
		// cache the video poster (first frame of the video).
		graphics.forEach((g) => {
			if (g.provider === 'capsule') return;
			if (
				g.animation.build_in === 0 || (g.animation.build_in === 2
					&& g.animation.delay_in === 0)
			) {
				const img = new Image();
				img.id = isVideo(g.images) ? (getVideoPoster(g.images) ?? '') : g.images;
				if (!document.getElementById(img.id)) {
					img.crossOrigin = 'anonymous'; // Stops CORS error from happening in incognito
					img.src = img.id;
					img.style.visibility = 'hidden';
					img.style.width = '0';
					img.style.height = '0';
					// Adding hidden img to the DOM works better than img.src by itself. img.src by itself
					// doesn't consistently load from disk cache in Chrome everytime, wasn't able to figure
					// out why. But when img is in the DOM, it consistently loads immediately everytime.
					document.body.appendChild(img);
				}
			}
		});
	}, [graphics]);

	const moveableTarget = useMemo((): HTMLDivElement | null => (
		selectedGraphic?.id
			? currentGraphicEl
			: null
	), [
		selectedGraphic?.id,
		isPreviewAnimationPlaying,
		isShotBuilderOpen,
		shotModalShot,
		moveableRef.current,
		currentGraphicEl,
	]);

	useEffect(() => {
		// want to update position of box after preview animation
		if (!isPreviewAnimationPlaying) {
			setTimeout(() => {
				moveableRef.current?.updateTarget();
			}, 30);
		}
	}, [isPreviewAnimationPlaying]);

	useEffect(() => {
		if (!canEdit || !selectedGraphic) return;

		const handleKeyDown = (e: KeyboardEvent) => {

			const active = document.activeElement;
			const isInput =
				active &&
				(
					active.tagName === 'INPUT' ||
					active.tagName === 'TEXTAREA' ||
					(active as HTMLElement).isContentEditable
				);

			// Only handle if not typing in input
			if (isInput) return;

			// Handle overlay nudge
			if (canEdit && selectedGraphic) {
				let dx = 0, dy = 0;
				let handled = false;

				switch (e.key) {
					case 'ArrowLeft':
						dx = -1;
						handled = true;
						break;
					case 'ArrowRight':
						dx = 1;
						handled = true;
						break;
					case 'ArrowUp':
						dy = -1;
						handled = true;
						break;
					case 'ArrowDown':
						dy = 1;
						handled = true;
						break;
					case 'Backspace':
					case 'Delete':
						handleRemoveGraphic();
						console.log('Removing graphic', selectedGraphic.id);
						handled = true;
						break;
					default:
						break;
				}

				if (handled) {
					if (dx !== 0 || dy !== 0) {
						nudgeSelectedOverlay(dx, dy);
					}
					e.preventDefault();
					e.stopPropagation();
				}
			}
			// If not handled, let event bubble for pagination
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [canEdit, selectedGraphic, nudgeSelectedOverlay]);

	const getIsHidden = useCallback((graphic: GraphicInterface): boolean => {
		// do not render overlays in location canvas view when showing location's hold slide
		if (showingHoldSlide) return true;

		const isEditing = roomState === ROOM_STATES.EDITING;

		if (isEditing) {
			return !!disabledShowBuilderOverlays?.includes(graphic?.id ?? 0);
		}

		return graphic?.id && enabledOverlays
			? !enabledOverlays.includes(graphic.id)
			: false;
	}, [enabledOverlays, roomState, disabledShowBuilderOverlays?.length, showingHoldSlide]);

	/*
		This introduces inherent risk since it's driven by front end timers
		Beehive should still handle the state as expected, but it's not ideal
		Overlay timers should be handled by Beehive
	*/
	const animatingOut = (id?: number) => {
		if (
			id
			&& meeting?.meeting
			&& token
			&& !animatedOverlays?.includes(id)
			&& shotStartTimestampEntity
			&& (
				currentShot.shot_data.shotGraphics?.includes(id)
				|| globalOverlays?.includes(id)
			)
		) {
			updateOverlayInAllShotOverlays(
				token,
				meeting.meeting,
				id,
				false,
			);
			updateOverlayInAllAnimatedOverlays(
				token,
				meeting.meeting,
				id,
			);
		}
	};

	const blockedId = roomState === ROOM_STATES.EDITING ? selectedGraphic?.id ?? 0 : 0;

	return (<>
		<div
			className={`custom-graphics-canvas can-edit-${canEdit}`}
			tabIndex={-1}
			data-testid='custom-graphics-canvas'
			onContextMenu={(e: React.MouseEvent) => handleRightClick(e)}
			ref={canvasRef}
		>
			{!isThumbnail && enablerState === EnablerStates.None && (
				<GreenBox isForShotEditorModal={isForShotEditorModal} />
			)}
			<Moveable
				target={moveableTarget}
				dragTarget={dragAreaRef.current}
				renderDirections={['se']}
				keepRatio={true}
				container={null}
				origin={false}
				ref={moveableRef}

				draggable={canEdit}
				onDrag={onDrag}
				onDragEnd={onDragEnd}

				rotatable={canEdit}
				onRotate={onRotate}
				onRotateEnd={onRotateEnd}

				resizable={canEdit}
				onResize={onResize}
				onResizeEnd={onResizeEnd}

				snappable={true}
				bounds={canvasBounds}

			// drag/resize/rotate also have an "onStart" func eg:
			// onResizeStart={onResizeStart}
			/>
			{canEdit && (
				// This permits dragging a selected graphic
				// by clicking anywhere on its bounding rect,
				// even if it's behind another graphic
				<div
					className="graphic-drag-area"
					style={{
						position: 'absolute',
						pointerEvents: selectedGraphic && !globalIsDragging ? 'auto' : 'none',
						zIndex: 1,
						top: `${selectedGraphic?.pos_y ?? 0}%`,
						left: `${selectedGraphic?.pos_x ?? 0}%`,
						width: `${selectedGraphic?.scale_x ?? 0}%`,
						height: `${selectedGraphic?.scale_y ?? 0}%`,
						transform: `rotate(${selectedGraphic?.rotate ?? 0}deg)`,
					}}
					ref={dragAreaRef}
				/>
			)}
			{shotGraphics.map((graphic: GraphicInterface) => (
				<CustomGraphic
					key={graphic.id}
					graphic={graphic}
					selectGraphic={canEdit ? updateSelectedGraphic : () => { }}
					location={location}
					isForShotEditorModal={isForShotEditorModal}
					isHidden={getIsHidden(graphic)}
					isThumbnail={isThumbnail || ENABLER_THUMBNAIL_STATES.includes(enablerState)}
					animatingOutCallback={animatingOut}
					forceUpdateMovable={() => moveableRef.current?.updateTarget()}
					replayBlocked={blockedId === graphic.id}
					updateCurrentGraphicEl={(el) => setCurrentGraphicEl(el)}
				/>
			))}
			{selectedGraphic?.id && rightClickCoords && host && meeting?.meeting && token && userId && (
				<GraphicsContextMenu
					socket={socket}
					contextMenuId={contextMenuId}
					clickCoordinates={rightClickCoords}
					selectedGraphic={selectedGraphic}
					shot={shot}
					isForShotEditorModal={isForShotEditorModal}
					renderedGraphics={renderedGraphics}
					handleRemoveGraphic={handleRemoveGraphic}
					handleSaveAndUpdate={(newGraphic: GraphicInterface) => {
						updateSelectedGraphic(newGraphic);
						dispatch(upsertAssociatedContent(token, meeting?.meeting, userId, newGraphic, currentShotId));
					}}
				/>
			)}
		</div>
	</>);

	function nudgeSelectedOverlay(dx: number, dy: number) {
		console.log('Nudging', dx, dy, selectedGraphic);
		if (!selectedGraphic) return;
		const newPosX = pxToPercent(percentToPx(selectedGraphic.pos_x ?? 0, canvasWidth) + dx, canvasWidth);
		const newPosY = pxToPercent(percentToPx(selectedGraphic.pos_y ?? 0, canvasHeight) + dy, canvasHeight);
		const updatedGraphic = {
			...selectedGraphic,
			pos_x: newPosX,
			pos_y: newPosY,
		};
		updateSelectedGraphic(updatedGraphic);

		if (token && meeting?.meeting && userId) {
			dispatch(upsertAssociatedContent(token, meeting.meeting, userId, updatedGraphic, currentShotId));
		}
	}
}
